import { generateResolver, Notification, STATUS_TYPES, Step, VALIDATORS, yup } from 'dyl-components';
import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Form, Portal } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import ImportSetup from './ImportSetup';
import ImportSetupFields from '../DataMapping/ImportSetupFields';
import DataMapping from '../DataMapping/DataMapping';
import MappingTable from '../DataMapping/MappingTable';
import { ImportFooter } from './ImportFooter';
import useWidthListener from 'shared/SettingsFooter/useWidthListener';
import useWindowWidth from 'shared/SettingsFooter/useWindowWidth';
import { useDispatch, useSelector } from 'react-redux';
import importActions from 'actions/import_data';
import { MathUtils } from 'utils';
import customFieldsActions from 'actions/custom_fields';
import pipelineActions from 'actions/pipeline';
import { ObjectUtils, AccountUtils, StringUtils, ValidationUtils } from '../../utils'
import { default as STEPS } from '../DataMapping/Steps'
import { DUPLICATE_OPTIONS, DYL_FIELDS_SCHEMA } from '../DataMapping/DYLFieldsConstants';
import FileUtils, { FILE_CATEGORIES } from 'utils/FileUtils';
import campaignActions from 'actions/campaigns';
import groupsActions from 'actions/groups';
import leadIngestionActions from 'actions/lead_ingestion';
import Results from "./results";
import ConfirmModal from 'shared/confirmation/ConfirmModal';
import CustomPrompt from 'shared/confirmation/CustomPrompt';
import { useConfirm } from 'shared/confirmation/useConfirm';
import contactsActions from 'actions/contacts';

const AddImport = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [groupOptions, setGroupOptions] = useState([]);   
    const [campaignListOptions, setCampaignListOptions] = useState([]);  
    const [isInLoading, setIsInLoading] = useState(false);
    const [recordsCount, setRecordsCount] = useState(0);

    const { pipelineCategories, isReadingPipelineCategories, groups, subgroups, isReadingGroups, campaignList, uploadId } = useSelector((state) => {
        const categories = state.pipeline.categories;
        return {
            pipelineCategories: categories.length === 1 ?
                categories[0].stages :
                categories.find((pipeline) => pipeline.primary)?.stages,
            isReadingPipelineCategories: state.pipeline.isReadingCategories,
            groups: state.groups.groups,
            subgroups: state.groups.subgroups,
            isReadingGroups: state.groups.isReadingGroups,
            campaignList: state.campaigns.campaignList,
            uploadId: state.import_data.uploadId,
        }
    });

    const { isReadingProductInterests, product_interests } = useSelector(state => state.lead_ingestion);

    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();
    const width = useWidthListener("settingsSidebar");
    const windowWidth = useWindowWidth();
    const navigate = useNavigate();
    const reader = new FileReader();

    const { control: importSetupControl, formState: { isValid: isImportSetupValid, isDirty: isImportSetupDirty }, watch, setValue, getValues } = useForm({
        mode: 'onChange',
        defaultValues: {
            description: '',
            module: null,
            duplicate_option: null,
            file: [],
            stage: null,
            match_type: ['email', 'full_name', 'phone'],
            assignmentType: 'unassigned',
            owners: [],
        },
        resolver: generateResolver({
            file: FileUtils.getFilesizeValidator(FILE_CATEGORIES.IMPORT),
            description: VALIDATORS.DESCRIPTION().required('This field is required'),
            module: yup.mixed().oneOf(['Lead', 'Contact', 'Opportunity', 'Customer']),
            duplicate_option: yup.mixed().required('This field is required').oneOf(DUPLICATE_OPTIONS.map(option => option.value)),
            stage: yup.mixed().when("module", {
                is: (stage) => stage !== 'Contact',
                then: schema => schema.required('This field is required'),
                otherwise: schema => schema.nullable(true)
            }),
        })
    });

    const { control: dataMappingControl, formState: { isValid: isDataMappingValid, isDirty: isDataMappingFormDirty }, setValue: setDataMappingValue, watch: dataMappingWatch, getValues: getDataMappingValues, trigger: dataMappingTrigger } = useForm({
        mode: 'onChange',
        defaultValues: {
            interests: [],
            group: { name: 'None', value: 'none' },
            campaign: 'none',
            fields: []
        },
        resolver: generateResolver({
            fields: DYL_FIELDS_SCHEMA
        })
    });

    const { fields, update: updateField } = useFieldArray({
        control: dataMappingControl,
        name: 'fields'
    });

    const watchedFields = dataMappingWatch('fields');
    const interestsSelectedAsDylField = watchedFields.findIndex(field => field?.dyl_field === "interests") !== -1;
    const isFieldSkipped = !(fields.length === dataMappingWatch()?.fields.filter((field) => field?.skipped).length);
    const productInterestsSelectedAsDylField = watchedFields.findIndex(field => field?.dyl_field === "product_interests") !== -1;

    const isNextStepEnabled = () => {
        if (currentStep === 0) {
            return isImportSetupValid && isImportSetupDirty && getValues("file").length === 1;
        }
        if (currentStep === 1) {
            return isDataMappingValid && isDataMappingFormDirty && isFieldSkipped && ValidationUtils.isMinimumFields(dataMappingWatch()).isValid;
        }
        if (currentStep === 2) {
            return true;
        }
    }

    const [module, duplicate_option] = watch(['module', 'duplicate_option']);

    const clearStage = () => {
        setValue('stage', null);
    }

    const onUpload = (files) => {
        if (files[0]) {
            setValue('description', files[0].name, { shouldValidate: true });
        }
    }

    const fieldOptions = useSelector(state => {
        return ObjectUtils.formatFieldsForDylSelect(state.custom_fields.mapping_fields)
    });

    

    const parseCSVForDataMapping = (fieldOptions) => {
        const file = getValues('file')[0];
        reader.onload = (e) => {
            const arrayBuffer = reader.result;

            const options = { type: 'array', raw: 'true' };
            const workbook = XLSX.read(arrayBuffer, options);

            const sheetName = workbook.SheetNames
            const sheet = workbook.Sheets[sheetName]
            const entries = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
            setRecordsCount(entries.length - 1);
            const headers = entries[0];
            const firstEntry = entries[1];

            setDataMappingValue('fields', headers.map((header, index) => {
               
            fieldOptions
                .filter(f => ['phone', 'email'].includes(f.field_type))
                 const normalizedHeader = StringUtils.sanitizeTerminology(
                    StringUtils.formatSearch(
                        StringUtils.sanitizeString(
                            (header || '').toLowerCase()
                        )
                    )
                );
                const customField = fieldOptions.find((option) => {
                    if (option?.disabled) {
                        return false;
                    }

                    const optionText = StringUtils.sanitizeAppendedValue(
                        (option?.text || '').toLowerCase()
                    );

                    const optionParent = option?.parent?.toLowerCase();

                    // Preserve the existing phone-specific validation
                    if (
                        option?.field_type === 'phone' &&
                        !normalizedHeader.includes('phone')
                    ) {
                        return false;
                    }

                    return (
                        normalizedHeader.includes(optionText) ||
                        (optionParent &&
                            normalizedHeader.includes(optionParent))
                    );
                });

                return {
                    name: header,
                    value: firstEntry[index] || "",
                    dyl_field: customField?.value || "",
                    field_type: customField?.field_type || "",
                    default_value: "",
                    duplicateProcess:
                        duplicate_option === "merge"
                            ? "keep"
                            : duplicate_option === "merge_only"
                            ? "replace"
                            : "keep",
                    skipped: false,
                    options: customField?.options ?? "",
                    valid_field: "import"
                };
            }));
            dataMappingTrigger('fields');
        }
        reader.readAsArrayBuffer(file);
    }

    const goToNextStep = async () => {
        const isNotFinalStep = currentStep < STEPS.length - 1;
        if (currentStep === 1) {
            const { file: files, description, module, duplicate_option, match_type, stage, owners } = getValues();
            const { fields, campaign, group, interests } = getDataMappingValues();
            const file = files[0];
            const formData = new FormData();
            const mapping = {};
            fields.forEach(field => {
                mapping[field.name] = {
                    default: field.default_value,
                    overwrite: field.duplicateProcess === 'replace',
                    skipped: field.skipped,
                    value: field.dyl_field
                }
            })
            if (interests?.length > 0) {
                mapping["interests"] = {
                    "default": interests,
                    "overwrite": false,
                    "skipped": false,
                    "value": "person.custom_data.fields.interests"
                }
            }
            formData.append('file', file, file.name);
            formData.append('file_name', file.name);
            formData.append('file_description', description);
            formData.append('mapping', JSON.stringify(mapping));
            const pipelineStage = pipelineCategories?.find((pipelineStage) => (pipelineStage.name === stage));
            try {
                await dispatch(importActions.addImport(formData, {
                    module,
                    duplicate_on: match_type?.join(","),
                    duplicate_option,
                    ...(campaign !== 'none' && { campaign_id: campaign }),
                    ...(group?.value !== 'none' && { group_id: group?.value }),
                    pipeline_stage_id: pipelineStage?.id,
                    ...(owners?.length > 0 && { owner_id: owners?.map(owner => owner.value)?.join(",") }),
                    row_count: recordsCount,
                }, null, {
                    "Accept": "application/json",
                    "Content-Type": "multipart/form-data",
                }));
                Notification.alert('Successfully added data mappings!', STATUS_TYPES.SUCCESS);
                setCurrentStep(currentStep + 1);
            } catch (e) {
                console.log(e);
                Notification.alert('Failed to add data mappings!', STATUS_TYPES.ERROR);
            }


        } else if (isNotFinalStep) {
            setCurrentStep(currentStep + 1);
            const isDataMappingStep = currentStep + 1 === 1;
            if (isDataMappingStep) {
                const { module } = getValues();
                dispatch(customFieldsActions.readMappingFields({ module: module === 'Contact' ? 'person' : module?.toLowerCase() })).then((response) => {
                    parseCSVForDataMapping(ObjectUtils.formatFields(response));
                });
            }
        }else{
            navigate('/settings/import-data');
        }
    }

    const goToPreviousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }

    const getSubgroups = async (parent_label_id) => {
        await dispatch(groupsActions.readSubgroups({ parent_label_id }));
    }

    useEffect(() => {
        dispatch(groupsActions.readGroups({ limit: 500 }));
        const pipelineStage = AccountUtils.getModulePipeline(module);
        if (pipelineStage) {
            dispatch(pipelineActions.getPipelineCategories({ account_stage: pipelineStage }));
        }
        dispatch(campaignActions.readCampaignsList());
        dispatch(leadIngestionActions.readProductInterests());
    }, [dispatch, module])

    useEffect(() => {
        let options = campaignList.map((campaign) => ({
            text: campaign.name,
            value: campaign.id,
            key: campaign.id
        }))
        options.unshift({ key: 'none', value: 'none', text: 'None'})
        setCampaignListOptions(options);
    }, [campaignList])

    useEffect(() => {
        const groupOptionsAux = groups.map((group) => {
            let subgroupsAux = [];
            if (subgroups[group.id] && subgroups[group.id].length > 0) {
                subgroupsAux = subgroups[group.id].map((subgroup) => ({text: subgroup.name, key: subgroup.id, value: subgroup.id}));
            }
            return {text: group.name, key: group.id, value: group.id, hasOptions: group.sub_labels, options: subgroupsAux, parent: group.parent_label_name};
        })
        groupOptionsAux.unshift({ key: 'none', value: 'none', text: 'None' })
        setGroupOptions(groupOptionsAux);
    }, [groups, subgroups]);

    useEffect(() => {
        dispatch(pipelineActions.getPipelineCategoriesForOpportunity({ account_stage: "opportunity" }));
        dispatch(contactsActions.getOwnersAndCoOwners())
    }, [dispatch]) 

    const checkIsInLoading = (isLoading) => {
        setIsInLoading(isLoading);
        return isLoading;
    }
    CustomPrompt(null, currentStep !== 2 && (isDataMappingFormDirty || isImportSetupDirty), isConfirmed, 'Changes not saved', 'Are you sure you want to exit?');

    return (
        <React.Fragment>
            <ConfirmModal />
            <Step.Group horizontal>
                {STEPS.map((step, index) => (
                    <Step key={step.title} completed={index < currentStep} active={index === currentStep && !isInLoading} title={step.title}>
                        {step.icon}
                    </Step>
                ))}
            </Step.Group>
            {currentStep === 0 && (
                <ImportSetup
                    control={importSetupControl}
                    module={module}
                    clearStage={clearStage}
                    duplicate_option={duplicate_option}
                    onUpload={onUpload}
                    importSetupFields={(
                        <ImportSetupFields
                            clearStage={clearStage}
                            control={importSetupControl}
                            duplicate_option={duplicate_option}
                            module={module}
                            pipelineCategories={pipelineCategories}
                            isReadingPipelineCategories={isReadingPipelineCategories}
                        />
                    )}
                    watch={watch}
                />
            )}
            {currentStep === 1 && (
                <DataMapping
                    dataMappingControl={dataMappingControl}
                    module={module}
                    interestsSelectedAsDylField={interestsSelectedAsDylField}
                    productInterestsSelectedAsDylField={productInterestsSelectedAsDylField}
                    groupOptions={groupOptions}
                    getSubgroups={getSubgroups}
                    campaignListOptions={campaignListOptions}
                    isReadingGroups={isReadingGroups}
                    importSetupFields={[
                        <Controller
                            control={importSetupControl}
                            name='description'
                            render={({ field: { value } }) => (
                                <Form.Field
                                    control='div'
                                    label='Source'
                                >
                                    <div>
                                        Import Data
                                    </div>
                                    <small>
                                        File: {value}
                                    </small>
                                </Form.Field>
                            )}
                        />,
                        <ImportSetupFields
                            clearStage={clearStage}
                            control={importSetupControl}
                            duplicate_option={duplicate_option}
                            module={module}
                            disable_module
                            pipelineCategories={pipelineCategories}
                            isReadingPipelineCategories={isReadingPipelineCategories}
                        />
                    ]}
                    mappingTable={(
                        <MappingTable
                            fields={fields}
                            isMerging={duplicate_option === 'merge' || duplicate_option === 'merge_only'}
                            dataMappingControl={dataMappingControl}
                            module={module}
                            updateField={updateField}
                            watchedFields={watchedFields}
                            fieldOptions={fieldOptions}
                            trigger={dataMappingTrigger}
                        />
                    )}
                    product_interests={product_interests}
                    isReadingProductInterests={isReadingProductInterests}
                />
            )}
            {currentStep === 2 && (
                <Results
                    module={module}
                    uploadId={uploadId}
                    getDataMappingValues={getDataMappingValues}
                    checkIsInLoading={checkIsInLoading}
                    recordsCount={recordsCount}
                />
            )}
            <Portal open>
                <ImportFooter
                    isMinimumFields={ValidationUtils.isMinimumFields(dataMappingWatch())}
                    currentStep={currentStep}
                    goToNextStep={goToNextStep}
                    isNextStepEnabled={isNextStepEnabled}
                    goToPreviousStep={goToPreviousStep}
                    STEPS={STEPS}
                    upload_id={uploadId}
                    width={MathUtils.calculatePercentage(windowWidth, windowWidth - width)}
                    {...(currentStep === 1 ? { customStep: 'Confirm Mapping' } : {})}
                />
            </Portal>
        </React.Fragment>
    )
};

export default AddImport;
